// Aignoz Innovation OS — Persona system (Phase 4)
// Single source of truth for persona configs. Pure client-side, no backend.

export const PERSONA_ORDER = [
  "founder", "researcher", "clinician", "investor",
  "incubator", "student", "enterprise", "guest",
];

export const PERSONAS = {
  founder: {
    name: "Founder",
    icon: "Rocket",
    accent: "#5EEAD4",
    accentRgb: "94,234,212",
    tagline: "Build the next healthcare venture",
    cmdCenterHero: "Your venture studio",
    cmdCenterSub: "From healthcare problem to venture-ready company in 15 minutes.",
    priorityTabs: ["overview", "innovation", "founder-review", "report", "pitch"],
    priorityWidgets: ["featured", "recent", "demo", "metrics", "trends", "categories"],
    priorityChips: [
      { label: "Generate a venture", to: "/projects/new" },
      { label: "Review founder critiques", venture: "founder-review" },
      { label: "Investor matching", soon: true },
    ],
    bannerLabel: "VIEWED THROUGH FOUNDER LENS",
  },
  researcher: {
    name: "Researcher",
    icon: "Microscope",
    accent: "#34D399",
    accentRgb: "52,211,153",
    tagline: "Discover and translate biology",
    cmdCenterHero: "Translate biology to ventures",
    cmdCenterSub: "Map convergences across biology, technology, and healthcare functions.",
    priorityTabs: ["innovation", "workflow", "prototype", "scorecard"],
    priorityWidgets: ["featured", "categories", "trends", "demo", "metrics", "recent"],
    priorityChips: [
      { label: "Open Innovation Engine", to: "/discover" },
      { label: "Explore biology matrix", to: "/discover/matrix" },
      { label: "Find tech convergences", to: "/discover/heatmap" },
    ],
    bannerLabel: "VIEWED THROUGH RESEARCHER LENS",
  },
  clinician: {
    name: "Clinician",
    icon: "Stethoscope",
    accent: "#FB7185",
    accentRgb: "251,113,133",
    tagline: "Bridge research to bedside",
    cmdCenterHero: "Bring trials into the real world",
    cmdCenterSub: "Validate, regulate, and pilot ventures with real-world clinical rigour.",
    priorityTabs: ["validation", "regulatory", "clinical", "founder-review"],
    priorityWidgets: ["featured", "categories", "recent", "trends", "metrics", "demo"],
    priorityChips: [
      { label: "Validation playbook", venture: "validation" },
      { label: "Regulatory pathways", venture: "regulatory" },
      { label: "Pilot matching", soon: true },
    ],
    bannerLabel: "VIEWED THROUGH CLINICIAN LENS",
  },
  investor: {
    name: "Investor",
    icon: "TrendingUp",
    accent: "#FBBF24",
    accentRgb: "251,191,36",
    tagline: "Find the next $1B venture",
    cmdCenterHero: "Find your next $1B healthcare bet",
    cmdCenterSub: "Score, compare, and de-risk healthcare ventures across TAM, TRL, and team.",
    priorityTabs: ["investor", "scorecard", "founder-review", "report"],
    priorityWidgets: ["featured", "metrics", "trends", "recent", "demo", "categories"],
    priorityChips: [
      { label: "Browse ventures", to: "/projects" },
      { label: "Compare scorecards", venture: "scorecard" },
      { label: "Deal flow alerts", soon: true },
    ],
    bannerLabel: "VIEWED THROUGH INVESTOR LENS",
  },
  incubator: {
    name: "Incubator",
    icon: "Boxes",
    accent: "#A78BFA",
    accentRgb: "167,139,250",
    tagline: "Accelerate the right startups",
    cmdCenterHero: "Match infrastructure to ventures",
    cmdCenterSub: "Spot ventures by TRL, capital intensity, and infrastructure needs.",
    priorityTabs: ["overview", "founder-review", "prototype", "regulatory"],
    priorityWidgets: ["featured", "recent", "metrics", "categories", "trends", "demo"],
    priorityChips: [
      { label: "TRL portfolio", to: "/projects" },
      { label: "Startup applications", soon: true },
      { label: "Infrastructure match", soon: true },
    ],
    bannerLabel: "VIEWED THROUGH INCUBATOR LENS",
  },
  student: {
    name: "Student",
    icon: "GraduationCap",
    accent: "#7DD3FC",
    accentRgb: "125,211,252",
    tagline: "Learn how innovation gets built",
    cmdCenterHero: "Learn how venture engineering works",
    cmdCenterSub: "Walk a real venture from problem → prototype → pitch in 15 minutes.",
    priorityTabs: ["workflow", "pitch", "innovation", "overview"],
    priorityWidgets: ["featured", "demo", "trends", "categories", "metrics", "recent"],
    priorityChips: [
      { label: "How it works", to: "/discover" },
      { label: "Walk through a venture", venture: "workflow" },
      { label: "Explore agent workflow", to: "/agents" },
    ],
    bannerLabel: "VIEWED THROUGH STUDENT LENS",
  },
  enterprise: {
    name: "Enterprise",
    icon: "Building2",
    accent: "#94A3B8",
    accentRgb: "148,163,184",
    tagline: "Scout strategic opportunities",
    cmdCenterHero: "Scout opportunities across your therapeutic portfolio",
    cmdCenterSub: "Map external R&D, partnerships, and acquisition targets in healthcare.",
    priorityTabs: ["overview", "innovation", "investor", "report"],
    priorityWidgets: ["featured", "trends", "categories", "metrics", "recent", "demo"],
    priorityChips: [
      { label: "Strategic scan", to: "/discover" },
      { label: "Pipeline overview", to: "/projects" },
      { label: "Portfolio mapping", soon: true },
    ],
    bannerLabel: "VIEWED THROUGH ENTERPRISE LENS",
  },
  guest: {
    name: "Guest",
    icon: "User",
    accent: "#94A3B8",
    accentRgb: "148,163,184",
    tagline: "Explore Aignoz Innovation OS",
    cmdCenterHero: "From healthcare problem to venture-ready stack",
    cmdCenterSub: "Powered by the Aignoz DeepTech Innovation Engine.",
    priorityTabs: ["overview", "innovation", "scorecard", "report"],
    priorityWidgets: ["featured", "metrics", "recent", "categories", "trends", "demo"],
    priorityChips: [
      { label: "Generate a venture", to: "/projects/new" },
      { label: "Open Innovation Engine", to: "/discover" },
      { label: "Open featured demo", venture: "overview", featured: true },
    ],
    bannerLabel: "VIEWED AS GUEST",
  },
};

// ---------- Showcase TRLs (hardcoded for demo ventures) ----------
const FEATURED_SEPSIS = "fc526bce-d199-433b-8475-8ba28829527f";
// AMR run id pattern — match anything that smells like the AMR case study.

export function computeTRL(doc) {
  const id = doc?.run_id || "";
  if (id === FEATURED_SEPSIS) {
    return { trl: 3, milestone: "Analytical Validation (DuoDx-LC v1 lactate/CRP)" };
  }
  const problem = (doc?.inputs?.problem || doc?.problem || "").toLowerCase();
  const company = (doc?.final_report?.company_name || "").toLowerCase();
  const looksLikeAmr = /amr|antimicrob|antibiot|resistance/.test(problem)
    || /resist|amr|zonesight/.test(company);
  if (looksLikeAmr) {
    return { trl: 3, milestone: "Analytical Validation (EUCAST RAST repeatability)" };
  }
  const outs = doc?.outputs || {};
  const hasClinical = !!outs.clinical_trial_designer;
  const hasReg = !!outs.regulatory_planner;
  const hasVal = !!outs.validation_planner;
  let trl = 2;
  if (hasClinical && hasReg) trl = 3;
  else if (hasVal) trl = 3;
  const vs = doc?.final_report?.validation_strategy;
  let milestone = "Analytical Validation";
  if (vs?.analytical_validation_plan) {
    const txt = String(vs.analytical_validation_plan).split(/\.|;/)[0];
    if (txt && txt.length > 6) milestone = txt.trim();
  }
  return { trl, milestone };
}

// ---------- Persona-specific banner callouts (derived from venture data) ----------
export function bannerCallouts(personaKey, doc) {
  const fin = doc?.final_report || {};
  const fr = doc?.founder_review || {};
  const sc = doc?.scores || {};
  const outs = doc?.outputs || {};
  const com = outs.commercialization_agent || {};
  const rs = outs.research_scientist || {};
  const pd = outs.prototype_designer || {};
  const trl = computeTRL(doc);

  const fmtUSD = (n) => {
    if (n == null) return "—";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n}`;
  };

  // Helper: extract top risk severity from founder review
  const regRisk = fr.regulatory_risk?.severity || fr.regulatory_risk?.level || "Medium";
  const commRisk = fr.commercialization_risk?.severity || fr.commercialization_risk?.level || "Medium";
  const clinRisk = fr.clinical_risk?.severity || fr.clinical_risk?.level || "Medium";

  // Helper: BoM sum
  let bomSum = 0;
  const bom = pd.bill_of_materials || pd.bom || [];
  if (Array.isArray(bom)) {
    for (const item of bom) {
      const c = item.cost_usd ?? item.cost ?? item.unit_cost_usd ?? 0;
      const q = item.quantity ?? item.qty ?? 1;
      bomSum += Number(c) * Number(q);
    }
  }
  const bomLabel = bomSum > 0 ? `$${Math.round(bomSum)} BoM` : "BoM est.";

  const techStack = fin.technology_stack || [];
  const techTriple = techStack.slice(0, 3).join(" × ") || "Multi-modal stack";

  const biomarkers = rs.biomarkers || rs.candidate_biomarkers || [];
  const topBiomarker = biomarkers[0]?.name || biomarkers[0] || "Phenotypic AST";

  const pilots = com.pilot_partners || com.launch_partners || [];
  const pilotSite = pilots[0]?.name || pilots[0] || "AIIMS Delhi";

  const validation = (fin.validation_strategy?.analytical_validation_plan
    || outs.validation_planner?.analytical_phase?.description
    || "EUCAST RAST 6-8h").toString().split(/\.|;/)[0];

  switch (personaKey) {
    case "founder":
      return [
        { label: `Top risk: ${regRisk.toString().toLowerCase()} regulatory delay`, tone: "warn" },
        { label: `MVP cost: ${bomLabel}`, tone: "info" },
        { label: "Refine pitch deck", to: "pitch", arrow: true },
      ];
    case "investor":
      return [
        { label: `TAM ${fmtUSD(com.tam_usd)} · Investor ${sc.investor_attractiveness_score?.score ?? "—"}`, tone: "info" },
        { label: `Top concern: commercialization ${commRisk}`, tone: "warn" },
        { label: "View memo", to: "report", arrow: true },
      ];
    case "researcher":
      return [
        { label: `Convergence: ${techTriple}`, tone: "info" },
        { label: `Top biomarker: ${topBiomarker}`, tone: "info" },
        { label: "Open Innovation Matrix", href: "/discover/matrix", arrow: true },
      ];
    case "clinician":
      return [
        { label: `Validation: ${validation.slice(0, 48)}`, tone: "info" },
        { label: `Adoption barrier: clinical ${clinRisk}`, tone: "warn" },
        { label: `Pilot site: ${pilotSite}`, tone: "info" },
      ];
    case "incubator":
      return [
        { label: `Current TRL: ${trl.trl}`, tone: "info" },
        { label: `Next milestone: ${trl.milestone.split(/[—()]/)[0].trim().slice(0, 40)}`, tone: "info" },
        { label: "Infra need: 3D printing + ISO 13485", tone: "warn" },
      ];
    case "student":
      return [
        { label: "12 AI agents collaborated on this venture", tone: "info" },
        { label: "Walk the pipeline", to: "workflow", arrow: true },
        { label: "Innovation methodology", href: "/discover", arrow: true },
      ];
    case "enterprise": {
      const cat = (techStack[0] || "diagnostics").toLowerCase();
      const months = outs.clinical_trial_designer?.timeline_months
        || outs.clinical_trial_designer?.estimated_duration_months
        || 16;
      return [
        { label: `Strategic fit: ${cat} portfolio`, tone: "info" },
        { label: "Comparable play: Cepheid GeneXpert", tone: "info" },
        { label: `Roadmap: ${months} months to market`, tone: "info" },
      ];
    }
    default:
      return [
        { label: `Innovation ${sc.innovation_score?.score ?? "—"}`, tone: "info" },
        { label: `TAM ${fmtUSD(com.tam_usd)}`, tone: "info" },
        { label: "Open full report", to: "report", arrow: true },
      ];
  }
}
